import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface NavigationContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: ReactNode;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(max-width: 899.95px)').matches
  );
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  useEffect(() => {
    const mobileWidth = window.matchMedia('(max-width: 899.95px)');
    const closeOnMobile = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false);
    };
    mobileWidth.addEventListener('change', closeOnMobile);
    return () => mobileWidth.removeEventListener('change', closeOnMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <NavigationContext.Provider
      value={{
        sidebarOpen,
        toggleSidebar,
        setSidebarOpen,
        breadcrumbs,
        setBreadcrumbs,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
